import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import { useHistory } from "react-router-dom";
import { helper } from "@src/controls/controlHelper";
import { Variation, VariationCombination } from "../types";

const PAGE_ID = 83;

export const useProductForm = (query: any) => {
  const [loading, setLoading] = useState(false);
  const [variations, setVariations] = useState<Variation[]>([]);
  const [draggedItem, setDraggedItem] = useState<{
    variationKey: number;
    index: number;
  } | null>(null);
  const [combinations, setCombinations] = useState<VariationCombination[]>([]);
  const [activeTab, setActiveTab] = useState("1");
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [useVariationShipping, setUseVariationShipping] = useState(false);
  const [product, setProduct] = useState<any>({});
  const [init, setInit] = useState(false);
  const history = useHistory();

  // Generate or sync combinations whenever variations change
  useEffect(() => {
    if (variations.length === 0) {
      setCombinations([]);
      return;
    }

    const normalizeVariationValues = (
      combination: VariationCombination
    ): string[] => {
      if (Array.isArray(combination.variationValues)) {
        return combination.variationValues;
      }
      const legacyValues = [combination.variation1, combination.variation2].filter(
        (value): value is string => typeof value === "string" && value.trim() !== ""
      );
      return legacyValues;
    };

    const buildKey = (variationValues: string[]): string =>
      JSON.stringify(variationValues);

    const buildName = (variationValues: string[]): string =>
      variationValues.join("-");

    const buildPrefixKey = (
      variationValues: string[],
      prefixLength: number
    ): string => buildKey(variationValues.slice(0, prefixLength));

    const cartesianProduct = (optionGroups: string[][]): string[][] => {
      return optionGroups.reduce<string[][]>(
        (acc, groupOptions) => {
          if (acc.length === 0) return groupOptions.map((value) => [value]);
          const next: string[][] = [];
          acc.forEach((prefix) => {
            groupOptions.forEach((value) => {
              next.push([...prefix, value]);
            });
          });
          return next;
        },
        []
      );
    };

    const validVariations = variations
      .map((variation) => ({
        name: variation.name,
        options: variation.options
          .filter((option) => option.value.trim() !== "")
          .map((option) => option.value),
      }))
      .filter((variation) => variation.options.length > 0);

    if (validVariations.length === 0) {
      setCombinations([]);
      return;
    }

    setCombinations((previousCombinations) => {
      const existingByKey = new Map<string, VariationCombination>();
      const existingByPrefixKey = new Map<string, VariationCombination>();

      previousCombinations.forEach((combination) => {
        const existingValues = normalizeVariationValues(combination);
        if (existingValues.length === 0) return;

        existingByKey.set(buildKey(existingValues), combination);

        for (let prefixLength = 1; prefixLength < existingValues.length; prefixLength += 1) {
          const prefixKey = buildPrefixKey(existingValues, prefixLength);
          if (!existingByPrefixKey.has(prefixKey)) {
            existingByPrefixKey.set(prefixKey, combination);
          }
        }
      });

      const optionGroups = validVariations.map((variation) => variation.options);
      const nextVariationValuesList = cartesianProduct(optionGroups);

      const nextCombinations = nextVariationValuesList.map((variationValues) => {
        const exactKey = buildKey(variationValues);
        const existingExact = existingByKey.get(exactKey);
        if (existingExact) {
          return {
            ...existingExact,
            variationValues,
            variation1: variationValues[0],
            variation2: variationValues[1],
            name: buildName(variationValues),
          };
        }

        const prefixKey = buildPrefixKey(
          variationValues,
          Math.max(1, variationValues.length - 1)
        );
        const existingByPrefix = existingByPrefixKey.get(prefixKey);

        if (existingByPrefix) {
          return {
            ...existingByPrefix,
            id: undefined,
            variationValues,
            variation1: variationValues[0],
            variation2: variationValues[1],
            name: buildName(variationValues),
          };
        }

        return {
          name: buildName(variationValues),
          variationValues,
          variation1: variationValues[0],
          variation2: variationValues[1],
          price: 0,
          origin_price: 0,
          stock: 0,
          sku: "",
          image: "",
          shipping: {},
        };
      });

      return nextCombinations;
    });
  }, [variations]);

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const isBottom = scrollTop + windowHeight >= documentHeight - 20;
      setIsAtBottom(isBottom);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Form submission
  const onFinish = async (values: any) => {
    try {
      // Validate variations
      if (variations.length === 0) {
        message.error("Vui lòng thêm ít nhất một nhóm phân loại!");
        return;
      }

      const hasEmptyOptions = variations.some(
        (v) =>
          v.options.length === 0 || v.options.every((opt) => !opt.value.trim())
      );

      if (hasEmptyOptions) {
        message.error("Mỗi nhóm phân loại phải có ít nhất một tùy chọn!");
        return;
      }

      const hasEmptyNames = variations.some((v) => !v.name.trim());
      if (hasEmptyNames) {
        message.error("Vui lòng nhập tên cho tất cả các nhóm phân loại!");
        return;
      }

      setLoading(true);

      // Validate weight
      if (!useVariationShipping) {
        const weight = Number(values.weight);
        if (isNaN(weight)) {
          message.error("Trọng lượng không hợp lệ");
          return;
        }
      } else {
        const invalidCombination = combinations.some((combo) => {
          const weight = Number(combo.shipping?.weight);
          return isNaN(weight) || weight <= 0;
        });

        if (invalidCombination) {
          message.error(
            "Vui lòng nhập trọng lượng hợp lệ cho tất cả các phân loại"
          );
          return;
        }
      }

      // Prepare data
      const formattedVariations = variations.map((variation) => ({
        name: variation.name,
        options: variation.options.map((opt) => ({
          id: opt.id,
          value: opt.value,
        })),
      }));

      const formattedCombinations = combinations.map((combo) => {
        const variationValues = Array.isArray(combo.variationValues)
          ? combo.variationValues
          : [combo.variation1, combo.variation2].filter(
              (value): value is string =>
                typeof value === "string" && value.trim() !== ""
            );

        const baseCombo = {
          ...(combo.id && { id: combo.id }),
          variationValues,
          variation1: variationValues[0] ?? combo.variation1,
          variation2: variationValues[1] ?? combo.variation2,
          price: combo.price,
          origin_price: combo.origin_price,
          stock: combo.stock,
          sku: combo.sku || "",
          image: combo.image || "",
        };

        if (useVariationShipping) {
          return {
            ...baseCombo,
            shipping: {
              weight: Number(combo.shipping?.weight),
              dimensions: {
                length: Number(combo.shipping?.dimensions?.length) || undefined,
                width: Number(combo.shipping?.dimensions?.width) || undefined,
                height: Number(combo.shipping?.dimensions?.height) || undefined,
              },
            },
          };
        }

        return baseCombo;
      });

      const shippingData = !useVariationShipping
        ? {
            shipping: {
              weight: Number(values.weight),
              dimensions: {
                length: Number(values.length) || undefined,
                width: Number(values.width) || undefined,
                height: Number(values.height) || undefined,
              },
            },
          }
        : {};

      const dataForm = {
        ...(product.id && { id: product.id }),
        ...(product.itemId && { itemId: product.itemId }),
        ...(product.createdAt && { createdAt: product.createdAt }),
        ...(product.updatedAt && { updatedAt: product.updatedAt }),

        name: values.name,
        description: values.description,
        categoryId: values.categoryId,
        categoryStoreId: values.categoryStoreId,
        images: values.images,
        thumbnail: values.thumbnail,
        weight: Number(values.weight),
        originPrice: values.originPrice,
        price: values.price,
        variations: formattedVariations,
        combinations: formattedCombinations,
        isActive: values.isActive,
        isApprove: values.isApprove,
        tags: values.tags || [],
        isHot: values.isHot,
        hotSequence: values.hotSequence || 0,
        sequence: values.sequence || 0,
        ...shippingData,
      };

      const cleanDataForm = Object.fromEntries(
        Object.entries(dataForm).filter(
          ([_, value]) => value !== null && value !== undefined && value !== ""
        )
      );

      // API call
      if (query?.id) {
        const pageInfo = await helper.getPage(PAGE_ID);
        const rs: any = await helper.callPageApi(pageInfo, "update_product", {
          ...cleanDataForm,
          id: query.id,
        });

        if (rs.status !== 200 && rs?.data?.code !== 0) throw rs;
        helper.alert("Update product successfully", "success");
      } else {
        const pageInfo = await helper.getPage(PAGE_ID);
        const rs: any = await helper.callPageApi(
          pageInfo,
          "create_product",
          cleanDataForm
        );

        if (rs.status !== 200 && rs?.data?.code !== 0) throw rs;
        helper.alert("Create product successfully", "success");
      }

      history.goBack();
    } catch (error: any) {
      console.log(error);
      if (error.message) {
        message.error(error.message);
      }
      helper.alert(
        query?.id ? "Update product failed" : "Create product failed",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Variation management
  const addVariation = () => {
    setVariations([
      ...variations,
      {
        key: Date.now(),
        name: "",
        options: [{ id: Date.now(), value: "" }],
      },
    ]);
  };

  const removeVariation = (key: number) => {
    if (variations.length > 1) {
      setVariations(variations.filter((v) => v.key !== key));
    } else {
      message.error("Phải có ít nhất một nhóm phân loại!");
    }
  };

  const handleVariationNameChange = (key: number, value: string) => {
    setVariations(
      variations.map((v) => (v.key === key ? { ...v, name: value } : v))
    );
  };

  const removeOption = (variationKey: number, optionId: number) => {
    setVariations(
      variations.map((v) => {
        if (v.key === variationKey) {
          if (v.options.length > 1) {
            return {
              ...v,
              options: v.options.filter((opt) => opt.id !== optionId),
            };
          } else {
            message.error("Mỗi nhóm phân loại phải có ít nhất một tùy chọn!");
            return v;
          }
        }
        return v;
      })
    );
  };

  const handleOptionChange = useCallback(
    (variationKey: number, optionId: number, value: string) => {
      setVariations((prev) =>
        prev.map((v) => {
          if (v.key === variationKey) {
            const isDuplicate = v.options.some(
              (opt) =>
                opt.id !== optionId &&
                opt.value.trim().toLowerCase() === value.trim().toLowerCase()
            );

            return {
              ...v,
              options: v.options.map((opt) =>
                opt.id === optionId
                  ? {
                      ...opt,
                      value,
                      error: isDuplicate
                        ? "Các giá trị phân loại phải khác nhau"
                        : undefined,
                    }
                  : opt
              ),
            };
          }
          return v;
        })
      );
    },
    []
  );

  const handleOptionKeyPress = (
    variationKey: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const variation = variations.find((v) => v.key === variationKey);
      if (variation && variation.options.length < 20) {
        const newOptionId = Date.now();
        setVariations(
          variations.map((v) => {
            if (v.key === variationKey) {
              return {
                ...v,
                options: [...v.options, { id: newOptionId, value: "" }],
              };
            }
            return v;
          })
        );

        setTimeout(() => {
          const newInput = document.querySelector(
            `input[data-option-id="${newOptionId}"]`
          ) as HTMLInputElement;
          if (newInput) {
            newInput.focus();
          }
        }, 0);
      }
    }
  };

  // Drag and drop
  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    optionKey: string
  ) => {
    setDraggedItem({ variationKey: Number(optionKey), index: 0 });
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetOptionKey: string
  ) => {
    if (draggedItem && draggedItem.variationKey === Number(targetOptionKey)) {
      // Handle move option logic
    }
    setDraggedItem(null);
  };

  // Combination management
  const handleCombinationChange = useCallback(
    (index: number, field: keyof any, value: any) => {
      setCombinations((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      );
    },
    []
  );

  const applyToAll = (
    price: number,
    origin_price: number,
    stock: number,
    sku: string,
    weight?: number,
    length?: number,
    width?: number,
    height?: number
  ) => {
    setCombinations((prev) =>
      prev.map((item) => ({
        ...item,
        price: price || item.price,
        origin_price: origin_price || item.origin_price,
        stock: stock || item.stock,
        sku: sku || item.sku,
        ...(useVariationShipping &&
          weight && {
            shipping: {
              ...item.shipping,
              weight: weight,
              dimensions: {
                length: length || item.shipping?.dimensions?.length,
                width: width || item.shipping?.dimensions?.width,
                height: height || item.shipping?.dimensions?.height,
              },
            },
          }),
      }))
    );
  };

  // Product info fetching
  const getProductInfo = async () => {
    try {
      const _pageInfo = await helper.getPage(PAGE_ID);
      const rs: any = await helper.callPageApi(_pageInfo, "get_product_info", {
        productTypeId: query?.id,
      });

      if (rs?.code === 403) {
        message.error("You do not have permission to access this resource");
        history.goBack();
        return;
      }

      if (rs?.data?.data) {
        const data = rs.data.data;

        // Lưu toàn bộ data vào state product
        setProduct(data);
        setInit(true);

        // Khởi tạo variations và combinations từ data
        if (data.variations && data.combinations) {
          const formattedVariations = data.variations.map((variation: any) => ({
            key: Date.now() + Math.random(),
            name: variation.name,
            options: variation.options.map((opt: any) => ({
              id: opt.id,
              value: opt.value,
            })),
          }));
          setVariations(formattedVariations);

          const formattedCombinations = data.combinations.map((combo: any) => ({
            id: combo.id,
            name: Array.isArray(combo.variationValues)
              ? combo.variationValues.join("-")
              : combo.variation1 +
                (combo.variation2 ? `-${combo.variation2}` : ""),
            variationValues: Array.isArray(combo.variationValues)
              ? combo.variationValues
              : [combo.variation1, combo.variation2].filter(
                  (value: any) => typeof value === "string" && value.trim() !== ""
                ),
            variation1: combo.variation1 || "",
            variation2: combo.variation2 || "",
            price: combo.price || 0,
            origin_price: combo.origin_price || 0,
            stock: combo.stock || 0,
            sku: combo.sku || "",
            image: combo.image || "",
            shipping: combo.shipping
              ? {
                  weight: combo.shipping.weight,
                  dimensions: {
                    length: combo.shipping.dimensions?.length,
                    width: combo.shipping.dimensions?.width,
                    height: combo.shipping.dimensions?.height,
                  },
                }
              : undefined,
          }));

          setCombinations(formattedCombinations);

          const hasShippingInfo = formattedCombinations.some(
            (combo: any) =>
              combo.shipping?.weight ||
              combo.shipping?.dimensions?.length ||
              combo.shipping?.dimensions?.width ||
              combo.shipping?.dimensions?.height
          );
          setUseVariationShipping(hasShippingInfo);
        }
      }
    } catch (error: any) {
      console.log("Error fetching product info:", error);

      if (error?.code === 403) {
        message.error("You do not have permission to access this resource");
        history.goBack();
        return;
      }

      helper.alert("Error loading product information", "error");
    }
  };

  // Initialize data
  useEffect(() => {
    if (query?.id) {
      getProductInfo();
    } else {
      setInit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (product?.variations && product?.combinations) {
      const formattedVariations = product.variations.map((variation: any) => ({
        key: Date.now() + Math.random(),
        name: variation.name,
        options: variation.options.map((opt: any) => ({
          id: opt.id,
          value: opt.value,
        })),
      }));
      setVariations(formattedVariations);

      const formattedCombinations = product.combinations.map((combo: any) => {
        const variationValues = Array.isArray(combo.variationValues)
          ? combo.variationValues
          : [combo.variation1, combo.variation2].filter(
              (value: any) => typeof value === "string" && value.trim() !== ""
            );

        return {
          id: combo.id,
          name: variationValues.join("-"),
          variationValues,
          variation1: combo.variation1 || variationValues[0] || "",
          variation2: combo.variation2 || variationValues[1] || "",
          price: combo.price || 0,
          origin_price: combo.origin_price || 0,
          stock: combo.stock || 0,
          sku: combo.sku || "",
          image: combo.image || "",
          shipping: combo.shipping
            ? {
                weight: combo.shipping.weight,
                dimensions: {
                  length: combo.shipping.dimensions?.length,
                  width: combo.shipping.dimensions?.width,
                  height: combo.shipping.dimensions?.height,
                },
              }
            : undefined,
        };
      });

      setCombinations(formattedCombinations);

      const hasShippingInfo = formattedCombinations.some(
        (combo: any) =>
          combo.shipping?.weight ||
          combo.shipping?.dimensions?.length ||
          combo.shipping?.dimensions?.width ||
          combo.shipping?.dimensions?.height
      );
      setUseVariationShipping(hasShippingInfo);
    }
  }, [product]);

  return {
    loading,
    variations,
    combinations,
    activeTab,
    isAtBottom,
    useVariationShipping,
    product,
    init,
    setActiveTab,
    setUseVariationShipping,
    onFinish,
    addVariation,
    removeVariation,
    handleVariationNameChange,
    removeOption,
    handleOptionChange,
    handleOptionKeyPress,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleCombinationChange,
    applyToAll,
  };
};
